import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Edit, UserX, UserCheck, Download, FileSpreadsheet, Trash2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, flattenObject } from '@/utils/exportCSV';
import { exportToExcel, flattenObjectForExcel, formatDataForExcel } from '@/utils/exportExcel';
import BulkActionBar from '../../components/BulkActionBar';
import ContactUsersModal from '../../components/admin/ContactUsersModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [skip, roleFilter]);

  const fetchUsers = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);

      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchUsers();
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(users.map(u => u.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (userId) => {
    if (selectedIds.includes(userId)) {
      setSelectedIds(selectedIds.filter(id => id !== userId));
    } else {
      setSelectedIds([...selectedIds, userId]);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Bulk action execution
  const handleBulkAction = async (action, reason) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/admin/users/bulk-action`,
        { ids: selectedIds, action, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Successfully ${action}d ${result.success_count} user(s)`);
        fetchUsers(); // Refresh list
      }
      
      if (result.failed_count > 0) {
        toast.error(`Failed to ${action} ${result.failed_count} user(s)`);
      }
      
      return result;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} users`);
      throw error;
    }
  };

  const handleDisable = async (userId) => {
    if (!window.confirm('Are you sure you want to disable this user?')) return;
    
    const token = localStorage.getItem('token');
    const reason = prompt('Reason for disabling:');
    
    try {
      await axios.post(`${API}/admin/users/${userId}/disable`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User disabled successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to disable user');
    }
  };

  const handleEnable = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/users/${userId}/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User enabled successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to enable user');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      resource_manager: 'bg-blue-100 text-blue-800',
      project_manager: 'bg-green-100 text-green-800',
      super_admin: 'bg-purple-100 text-purple-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {role.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Users Management</h1>
        <p className="text-gray-600 mt-2">View and manage all platform users</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 flex items-center space-x-2">
              <Input
                placeholder="Search by email or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} style={{ backgroundColor: '#62D84E' }}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
            
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setSkip(0);
              }}
              className="border rounded-md px-3 py-2"
            >
              <option value="">All Roles</option>
              <option value="resource_manager">Resource Manager</option>
              <option value="project_manager">Project Manager</option>
              <option value="operating_partner">Operating Partner</option>
              <option value="super_admin">Super Admin</option>
            </select>

            <Button variant="outline" onClick={() => {
              const flattenedUsers = users.map(user => flattenObject(user));
              exportToCSV(flattenedUsers, 'users_export');
              toast.success('Users exported to CSV successfully');
            }}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>

            <Button variant="outline" onClick={() => {
              const flattenedUsers = users.map(user => flattenObjectForExcel(user));
              const formattedUsers = formatDataForExcel(flattenedUsers, {
                id: 'User ID',
                email: 'Email',
                role: 'Role',
                status: 'Status',
                company_name: 'Company Name',
                created_at: 'Registration Date'
              });
              exportToExcel(formattedUsers, `users_export_${new Date().toISOString().split('T')[0]}`, 'Users');
              toast.success('Users exported to Excel successfully');
            }}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>

            {/* Contact Selected Users Button */}
            <Button 
              variant="outline"
              onClick={() => setShowContactModal(true)}
              disabled={selectedIds.length === 0}
              className={selectedIds.length > 0 ? 'border-primary text-primary hover:bg-primary/10' : ''}
              data-testid="contact-users-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === users.length && users.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map(({ user, company }) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(user.id)}
                          onChange={() => handleSelectOne(user.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                      <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.is_freelance 
                            ? (user.freelance_model === 'represented' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-orange-100 text-orange-800')
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.is_freelance 
                            ? (user.freelance_model === 'represented' ? 'Freelance (Represented)' : 'Freelance (Independent)')
                            : (user.role === 'resource_manager' ? 'RM (Company)' : 'Company')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{company?.name || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {user.status !== 'disabled' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDisable(user.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEnable(user.id)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <UserCheck className="w-4 h-4" />
                          </Button>
                        )}
                        <Link to={`/admin/users/${user.id}`}>
                          <Button
                            size="sm"
                            variant="ghost"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} users
        </p>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClearSelection={handleClearSelection}
        actions={[
          { label: 'Disable', value: 'disable', color: 'text-orange-600 border-orange-600', icon: UserX },
          { label: 'Enable', value: 'enable', color: 'text-green-600 border-green-600', icon: UserCheck },
          { label: 'Delete', value: 'delete', color: 'text-red-600 border-red-600', icon: Trash2 }
        ]}
        onExecute={handleBulkAction}
        entityType="users"
      />

      {/* Contact Users Modal */}
      <ContactUsersModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        selectedUsers={users
          .filter(({ user }) => selectedIds.includes(user.id))
          .map(({ user, company }) => ({
            id: user.id,
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            company_name: company?.name
          }))}
        onSuccess={() => {
          setSelectedIds([]);
        }}
      />
    </div>
  );
};

export default UsersPage;